import { supabase } from '@/lib/supabase';
import { checkRateLimit, rateLimitMessage } from '@/lib/coreRateLimit';
import { runCoreAction } from '@/lib/coreActions';
import { generateIdempotencyKey } from '@/lib/coreIdempotency';
import { emitCoreActivity } from '@/lib/coreEventHooks';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 45. AI READY
// Zifek Core est conçu pour Zifek AI, mais Zifek AI n'accède JAMAIS
// directement à la base. Le Core expose des services internes utilisables
// par le « Zifek AI Tool Gateway » :
//
//   AI → search_customers() → Authorization → Zifek Core → Validated result
//
// Même principe pour : search_businesses, search_services, search_products,
// search_accommodations, search_activities, get_customer_orders,
// get_customer_appointments, get_customer_bookings.
//
// Chaque appel : authorization (permission) → validation (params) →
// exécution → résultat validé → journalisation (core_ai_tool_calls).
// ─────────────────────────────────────────────────────────────

export interface AiToolDef {
  id: number;
  tool_key: string;
  label: string;
  description: string | null;
  category: string | null;
  required_permission: string | null;
  entity_type: string | null;
  returns: string | null;
  is_active: boolean | null;
}

/** Catalogue de secours (si la table n'est pas encore chargée). */
export const AI_TOOLS_FALLBACK: Omit<AiToolDef, 'id'>[] = [
  { tool_key: 'search_customers', label: 'Rechercher des clients', description: 'Recherche les fiches clients par nom, email ou téléphone.', category: 'search', required_permission: 'customers.read', entity_type: 'customer', returns: 'Liste de clients', is_active: true },
  { tool_key: 'search_businesses', label: 'Rechercher des entreprises', description: 'Recherche les entreprises/établissements du socle.', category: 'search', required_permission: 'settings.read', entity_type: 'business', returns: "Liste d'entreprises", is_active: true },
  { tool_key: 'search_services', label: 'Rechercher des services', description: 'Recherche dans le catalogue de services.', category: 'search', required_permission: 'services.read', entity_type: 'service', returns: 'Liste de services', is_active: true },
  { tool_key: 'search_products', label: 'Rechercher des produits', description: 'Recherche dans le catalogue de produits.', category: 'search', required_permission: 'products.read', entity_type: 'product', returns: 'Liste de produits', is_active: true },
  { tool_key: 'search_accommodations', label: 'Rechercher des hébergements', description: 'Recherche les hébergements (propriétés).', category: 'search', required_permission: 'products.read', entity_type: 'accommodation', returns: "Liste d'hébergements", is_active: true },
  { tool_key: 'search_activities', label: 'Rechercher des activités', description: 'Recherche les activités et créneaux.', category: 'search', required_permission: 'services.read', entity_type: 'activity', returns: "Liste d'activités", is_active: true },
  { tool_key: 'search_professionals', label: 'Rechercher un professionnel', description: 'Recherche les professionnels publiés (architecte, coiffeur…).', category: 'search', required_permission: 'services.read', entity_type: 'professional', returns: 'Liste de professionnels', is_active: true },
  { tool_key: 'check_availability', label: 'Vérifier une disponibilité', description: 'Contrôle les créneaux disponibles d’un service.', category: 'read', required_permission: 'services.read', entity_type: 'availability', returns: 'Créneaux disponibles', is_active: true },
  { tool_key: 'get_reviews', label: 'Lire les avis', description: 'Renvoie les avis publiés d’une entité (entreprise, service, produit).', category: 'read', required_permission: 'services.read', entity_type: 'review', returns: "Liste d'avis", is_active: true },
  { tool_key: 'get_customer_orders', label: "Commandes d'un client", description: 'Renvoie les commandes liées à un client.', category: 'read', required_permission: 'orders.read', entity_type: 'order', returns: 'Liste de commandes', is_active: true },
  { tool_key: 'get_customer_appointments', label: "Rendez-vous d'un client", description: 'Renvoie les rendez-vous d’un client.', category: 'read', required_permission: 'services.read', entity_type: 'appointment', returns: 'Liste de rendez-vous', is_active: true },
  { tool_key: 'get_customer_bookings', label: "Réservations d'un client", description: 'Renvoie les réservations d’un client.', category: 'read', required_permission: 'products.read', entity_type: 'booking', returns: 'Liste de réservations', is_active: true },
  { tool_key: 'create_appointment', label: 'Créer un rendez-vous', description: 'Crée un rendez-vous via l’Action Layer : autorisation + idempotence garanties.', category: 'write', required_permission: 'appointment.create', entity_type: 'appointment', returns: 'Rendez-vous créé', is_active: true },
  { tool_key: 'create_booking', label: 'Créer une réservation', description: 'Réserve un hébergement (bien entier ou unité) via l’Action Layer : autorisation + idempotence garanties.', category: 'write', required_permission: 'products.write', entity_type: 'booking', returns: 'Réservation créée', is_active: true },
  { tool_key: 'create_invoice', label: 'Créer une facture', description: 'Émet une facture via l’Action Layer : autorisation + idempotence garanties.', category: 'write', required_permission: 'finance.write', entity_type: 'invoice', returns: 'Facture créée', is_active: true },
];

export async function listAiTools(): Promise<AiToolDef[]> {
  const { data, error } = await supabase
    .from('core_ai_tools')
    .select('*')
    .order('category', { ascending: true })
    .order('tool_key', { ascending: true });
  if (error) throw error;
  return (data || []) as AiToolDef[];
}

export async function setAiToolActive(id: number, active: boolean): Promise<void> {
  const { error } = await supabase.from('core_ai_tools').update({ is_active: active }).eq('id', id);
  if (error) throw error;
}

// ── Contexte d'invocation fourni par la passerelle ──

export interface AiToolContext {
  tenantId: number | null;
  organizationId: number;
  commerceId: number;
  userId?: number | null;
  /** Contrôle d'autorisation : renvoie true si l'appelant possède la permission. */
  canAccess: (permission: string) => boolean;
  source?: string;
}

export interface AiToolResult {
  ok: boolean;
  authorized: boolean;
  tool: string;
  data: unknown[];
  count: number;
  error?: string;
}

function cleanStr(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Valide et exécute un outil. Ne lève jamais : renvoie un résultat normalisé. */
export async function invokeAiTool(
  toolKey: string,
  params: Record<string, unknown>,
  ctx: AiToolContext,
): Promise<AiToolResult> {
  const started = Date.now();
  const base: AiToolResult = { ok: false, authorized: false, tool: toolKey, data: [], count: 0 };

  const log = async (opts: { authorized: boolean; status: string; count: number; error?: string }) => {
    try {
      await supabase.from('core_ai_tool_calls').insert({
        tenant_id: ctx.tenantId,
        organization_id: ctx.organizationId,
        tool_key: toolKey,
        input: params,
        result_count: opts.count,
        status: opts.status,
        authorized: opts.authorized,
        actor_user_id: ctx.userId ?? null,
        source: ctx.source || 'gateway',
        error: opts.error || null,
        duration_ms: Date.now() - started,
      });
    } catch {
      /* best-effort */
    }
  };

  // Rate limiting applicatif (spec 64) — par outil, avant toute exécution.
  const rl = await checkRateLimit({
    organizationId: ctx.organizationId,
    tenantId: ctx.tenantId,
    scope: 'ai_tools',
    subjectType: 'tool',
    subjectKey: toolKey,
    endpoint: 'ai.invoke',
  });
  if (!rl.allowed) {
    await log({ authorized: false, status: 'rate_limited', count: 0, error: 'Limite atteinte' });
    return { ...base, error: rateLimitMessage(rl) };
  }

  // Récupère la définition (permission requise + activation).
  let def: AiToolDef | undefined;
  try {
    const { data } = await supabase.from('core_ai_tools').select('*').eq('tool_key', toolKey).maybeSingle();
    def = (data as AiToolDef) || undefined;
  } catch {
    def = undefined;
  }
  const fallback = AI_TOOLS_FALLBACK.find((t) => t.tool_key === toolKey);
  if (!def && !fallback) {
    await log({ authorized: false, status: 'unknown_tool', count: 0, error: 'Outil inconnu' });
    return { ...base, error: 'Outil inconnu.' };
  }
  const required = def?.required_permission ?? fallback?.required_permission ?? null;
  const active = def?.is_active ?? fallback?.is_active ?? true;
  if (!active) {
    await log({ authorized: false, status: 'disabled', count: 0, error: 'Outil désactivé' });
    return { ...base, error: 'Outil désactivé.' };
  }

  // ── Authorization ──
  if (required && !ctx.canAccess(required)) {
    await log({ authorized: false, status: 'forbidden', count: 0, error: `Permission ${required} requise` });
    return { ...base, error: `Permission « ${required} » requise.` };
  }

  const q = cleanStr(params.q || params.query);
  const customerId = params.customer_id != null ? Number(params.customer_id) : null;

  try {
    let rows: unknown[] = [];

    switch (toolKey) {
      // ── search_* ──
      case 'search_customers': {
        let query = supabase
          .from('core_customers')
          .select('id, display_name, email, phone, company_name, customer_type, status')
          .eq('organization_id', ctx.organizationId)
          .limit(25);
        if (q) query = query.or(`display_name.ilike.%${q}%,email.ilike.%${q}%,phone.ilike.%${q}%`);
        rows = ((await query).data || []);
        break;
      }
      case 'search_businesses': {
        let query = supabase
          .from('core_locations')
          .select('id, name, phone, is_primary, status')
          .eq('organization_id', ctx.organizationId)
          .limit(25);
        if (q) query = query.ilike('name', `%${q}%`);
        rows = ((await query).data || []);
        break;
      }
      case 'search_services': {
        let query = supabase
          .from('services')
          .select('id, titre, description, prix')
          .eq('idvendeur', ctx.commerceId)
          .limit(25);
        if (q) query = query.ilike('titre', `%${q}%`);
        rows = ((await query).data || []);
        break;
      }
      case 'search_products': {
        let query = supabase
          .from('product')
          .select('product_id, product_name, product_price, prix, status')
          .eq('owner', ctx.commerceId)
          .limit(25);
        if (q) query = query.ilike('product_name', `%${q}%`);
        rows = ((await query).data || []);
        break;
      }
      case 'search_accommodations': {
        let query = supabase
          .from('hebergement_properties')
          .select('id, name, city, price, currency, status')
          .eq('merchant_id', ctx.commerceId)
          .limit(25);
        if (q) query = query.ilike('name', `%${q}%`);
        rows = ((await query).data || []);
        break;
      }
      case 'search_activities': {
        let query = supabase
          .from('booking_services')
          .select('id, service_name, duration, location, statut')
          .eq('owner', ctx.commerceId)
          .limit(25);
        if (q) query = query.ilike('service_name', `%${q}%`);
        rows = ((await query).data || []);
        break;
      }
      case 'search_professionals': {
        // Annuaire PUBLIC uniquement : jamais de fiche privée (spec 79).
        let query = supabase
          .from('core_professionals')
          .select('id, display_name, profession, specialties, experience_years, languages, verification_status')
          .eq('public_profile', true)
          .eq('status', 'active')
          .limit(25);
        if (q) query = query.or(`display_name.ilike.%${q}%,profession.ilike.%${q}%,specialties.ilike.%${q}%`);
        rows = ((await query).data || []);
        break;
      }
      case 'check_availability': {
        const serviceId = params.service_id != null ? Number(params.service_id) : null;
        const date = cleanStr(params.date);
        if (!serviceId) {
          await log({ authorized: true, status: 'validation_error', count: 0, error: 'service_id requis' });
          return { ...base, authorized: true, error: 'Validation : service_id est requis.' };
        }
        const { data: avail } = await supabase
          .from('booking_availabilities')
          .select('id, day_of_week, start_time, end_time')
          .eq('service_id', serviceId);
        const windows = (avail || []).map((r) => {
          const row = r as Record<string, unknown>;
          return { type: 'window', day_of_week: row.day_of_week, start_time: row.start_time, end_time: row.end_time };
        });
        if (date) {
          const { data: booked } = await supabase
            .from('booking_appointments')
            .select('id, time, status')
            .eq('service_id', serviceId)
            .eq('date', date);
          rows = [{ type: 'day', date, booked_count: (booked || []).length }, ...windows];
        } else {
          rows = windows;
        }
        break;
      }
      case 'get_reviews': {
        const entityType = cleanStr(params.entity_type);
        const entityId = params.entity_id != null ? String(params.entity_id) : '';
        let query = supabase
          .from('core_reviews')
          .select('id, author_name, entity_type, entity_id, rating, title, comment, is_verified, created_at')
          .eq('status', 'published')
          .order('created_at', { ascending: false })
          .limit(50);
        if (entityType) query = query.eq('entity_type', entityType);
        if (entityId) query = query.eq('entity_id', entityId);
        if (!entityType && !entityId) query = query.eq('organization_id', ctx.organizationId);
        rows = ((await query).data || []);
        break;
      }
      // ── get_customer_* ──
      case 'get_customer_orders': {
        if (!customerId) {
          await log({ authorized: true, status: 'validation_error', count: 0, error: 'customer_id requis' });
          return { ...base, authorized: true, error: 'Validation : customer_id est requis.' };
        }
        const { data } = await supabase
          .from('commande')
          .select('id, nomclient, nom, totalttc, monaie, statut, created_at')
          .eq('idvendeur', ctx.commerceId)
          .eq('core_customer_id', customerId)
          .order('created_at', { ascending: false })
          .limit(50);
        rows = (data || []);
        break;
      }
      case 'get_customer_appointments': {
        if (!customerId) {
          await log({ authorized: true, status: 'validation_error', count: 0, error: 'customer_id requis' });
          return { ...base, authorized: true, error: 'Validation : customer_id est requis.' };
        }
        const { data: cust } = await supabase
          .from('core_customers')
          .select('display_name, email, phone')
          .eq('id', customerId)
          .maybeSingle();
        const c = cust as { display_name: string | null; email: string | null; phone: string | null } | null;
        if (!c) { rows = []; break; }
        let query = supabase
          .from('booking_appointments')
          .select('id, fullname, email, phone, date, time, status')
          .limit(50);
        if (c.email) query = query.ilike('email', c.email);
        else if (c.phone) query = query.eq('phone', c.phone);
        else if (c.display_name) query = query.ilike('fullname', `%${c.display_name}%`);
        rows = ((await query).data || []);
        break;
      }
      case 'get_customer_bookings': {
        if (!customerId) {
          await log({ authorized: true, status: 'validation_error', count: 0, error: 'customer_id requis' });
          return { ...base, authorized: true, error: 'Validation : customer_id est requis.' };
        }
        const { data } = await supabase
          .from('hebergement_reservations')
          .select('id, guest_name, guest_email, check_in, check_out, nights, status')
          .eq('merchant_id', ctx.commerceId)
          .eq('customer_id', customerId)
          .order('check_in', { ascending: false })
          .limit(50);
        rows = (data || []);
        break;
      }
      // ── Outil d'ÉCRITURE : create_appointment ──
      // Une écriture IA ne touche JAMAIS la base en direct : elle passe par
      // l'Action Layer (autorisation + idempotence) — spec 46/47/79.
      case 'create_appointment': {
        const serviceId = params.service_id != null ? Number(params.service_id) : null;
        const professionalId = params.professional_id != null ? Number(params.professional_id) : null;
        const fullname = cleanStr(params.fullname || params.name);
        const email = cleanStr(params.email);
        const phone = cleanStr(params.phone);
        const date = cleanStr(params.date);
        const time = cleanStr(params.time);
        const message = cleanStr(params.message || params.notes);
        // Idempotence : rejouer le même appel ne crée PAS un 2e rendez-vous.
        const idempotencyKey = cleanStr(params.idempotency_key) || generateIdempotencyKey('appt');

        const missing: string[] = [];
        if (!fullname) missing.push('fullname');
        if (!date) missing.push('date');
        if (!time) missing.push('time');
        if (missing.length > 0) {
          await log({ authorized: true, status: 'validation_error', count: 0, error: `Champs requis : ${missing.join(', ')}` });
          return { ...base, authorized: true, error: `Validation : ${missing.join(', ')} sont requis.` };
        }

        // Règle métier (spec 69) : pas de RDV sur un professionnel inexistant.
        if (professionalId != null) {
          const { data: prof } = await supabase
            .from('core_professionals')
            .select('id, status, deleted_at')
            .eq('id', professionalId)
            .maybeSingle();
          const p = prof as Record<string, unknown> | null;
          if (!p || p.deleted_at != null) {
            await log({ authorized: true, status: 'validation_error', count: 0, error: 'professional_id inexistant' });
            return { ...base, authorized: true, error: 'Validation : professional_id inexistant.' };
          }
        }

        // ── Action Layer : autorisation → idempotence → exécution → journal ──
        const action = await runCoreAction({
          actionKey: 'create_appointment',
          tenantId: ctx.tenantId,
          organizationId: ctx.organizationId,
          actorUserId: ctx.userId ?? null,
          actorName: 'Zifek AI',
          resourceType: 'appointment',
          permission: 'appointment.create',
          parameters: params,
          canAccess: ctx.canAccess,
          idempotencyKey,
          idempotencyScope: 'appointments',
          source: 'ai_tool',
          execute: async () => {
            const { data, error } = await supabase
              .from('booking_appointments')
              .insert({
                service_id: serviceId,
                fullname,
                email: email || null,
                phone: phone || null,
                date,
                time,
                message: message || null,
                status: 'pending',
              })
              .select('id, fullname, date, time, status')
              .maybeSingle();
            if (error) throw error;
            const row = data as Record<string, unknown> | null;
            const appointmentId = row?.id != null ? String(row.id) : null;

            // Relation Appointment → Professional (spec 75), best-effort.
            if (professionalId != null && appointmentId) {
              try {
                await supabase.from('core_entity_relations').insert({
                  tenant_id: ctx.tenantId,
                  organization_id: ctx.organizationId,
                  source_type: 'appointment',
                  source_id: appointmentId,
                  target_type: 'professional',
                  target_id: String(professionalId),
                  relation_type: 'assigned_to',
                  label: 'Professionnel assigné',
                });
              } catch {
                /* la relation est optionnelle */
              }
            }

            // Événement du socle → timeline + invalidation cache (best-effort).
            if (appointmentId) {
              try {
                await emitCoreActivity({
                  commerceId: ctx.commerceId,
                  eventKey: 'appointment.created',
                  entityType: 'appointment',
                  entityId: appointmentId,
                  title: 'Rendez-vous créé',
                  description: `${fullname} · ${date} ${time}`,
                  actorUserId: ctx.userId ?? null,
                  actorName: 'Zifek AI',
                  source: 'ai_tool',
                  payload: { professional_id: professionalId, service_id: serviceId },
                });
              } catch {
                /* le câblage d'événements ne bloque jamais l'écriture */
              }
            }

            return {
              entityType: 'appointment',
              entityId: appointmentId,
              result: { appointment: row, professional_id: professionalId },
            };
          },
        });

        if (!action.ok) {
          const denied = action.status === 'denied';
          await log({ authorized: !denied, status: denied ? 'forbidden' : 'error', count: 0, error: action.error });
          return { ...base, authorized: !denied, error: action.error || 'Action refusée par l’Action Layer.' };
        }

        rows = [{
          action_id: action.actionId,
          action_status: action.status,
          entity_id: action.entityId,
          idempotency_key: idempotencyKey,
          appointment: action.result,
        }];
        break;
      }
      // ── Outil d'ÉCRITURE : create_booking (hébergement) ──
      // Même modèle que create_appointment : aucune écriture directe, tout
      // passe par l'Action Layer (autorisation + idempotence) — spec 46/47/79.
      case 'create_booking': {
        const propertyId = params.property_id != null ? Number(params.property_id) : null;
        const unitId = params.unit_id != null ? Number(params.unit_id) : null;
        const guestName = cleanStr(params.guest_name || params.fullname || params.name);
        const guestEmail = cleanStr(params.email);
        const guestPhone = cleanStr(params.phone);
        const checkIn = cleanStr(params.check_in);
        const checkOut = cleanStr(params.check_out);
        const guests = params.guests != null ? Number(params.guests) : 1;
        const notes = cleanStr(params.notes || params.message);
        const linkCustomerId = params.customer_id != null ? Number(params.customer_id) : null;
        // Idempotence : rejouer le même appel ne crée PAS une 2e réservation.
        const idempotencyKey = cleanStr(params.idempotency_key) || generateIdempotencyKey('book');

        const missing: string[] = [];
        if (!propertyId) missing.push('property_id');
        if (!guestName) missing.push('guest_name');
        if (!checkIn) missing.push('check_in');
        if (!checkOut) missing.push('check_out');
        if (missing.length > 0) {
          await log({ authorized: true, status: 'validation_error', count: 0, error: `Champs requis : ${missing.join(', ')}` });
          return { ...base, authorized: true, error: `Validation : ${missing.join(', ')} sont requis.` };
        }
        if (checkOut <= checkIn) {
          await log({ authorized: true, status: 'validation_error', count: 0, error: 'dates invalides' });
          return { ...base, authorized: true, error: 'Validation : la date de départ doit suivre la date d’arrivée.' };
        }

        // Règle métier (spec 69) : le bien doit exister ET appartenir au commerce.
        const { data: propRow } = await supabase
          .from('hebergement_properties')
          .select('id, name, price, currency, merchant_id')
          .eq('id', propertyId)
          .maybeSingle();
        const prop = propRow as Record<string, unknown> | null;
        if (!prop || Number(prop.merchant_id) !== ctx.commerceId) {
          await log({ authorized: true, status: 'validation_error', count: 0, error: 'property_id inexistant' });
          return { ...base, authorized: true, error: 'Validation : property_id inexistant.' };
        }
        let pricePerNight = Number(prop.price) || 0;
        const currency = String(prop.currency || 'MAD');
        if (unitId != null) {
          const { data: unitRow } = await supabase
            .from('hebergement_units')
            .select('id, price, property_id, merchant_id')
            .eq('id', unitId)
            .maybeSingle();
          const unit = unitRow as Record<string, unknown> | null;
          if (!unit || Number(unit.property_id) !== propertyId) {
            await log({ authorized: true, status: 'validation_error', count: 0, error: 'unit_id inexistant' });
            return { ...base, authorized: true, error: 'Validation : unit_id inexistant pour ce bien.' };
          }
          pricePerNight = Number(unit.price) || pricePerNight;
        }

        // ── Action Layer : autorisation → idempotence → exécution → journal ──
        const action = await runCoreAction({
          actionKey: 'create_booking',
          tenantId: ctx.tenantId,
          organizationId: ctx.organizationId,
          actorUserId: ctx.userId ?? null,
          actorName: 'Zifek AI',
          resourceType: unitId != null ? 'room' : 'accommodation',
          resourceId: unitId != null ? `unit-${unitId}` : `property-${propertyId}`,
          permission: 'products.write',
          parameters: params,
          canAccess: ctx.canAccess,
          idempotencyKey,
          idempotencyScope: 'reservations',
          source: 'ai_tool',
          execute: async () => {
            const { data, error } = await supabase.rpc('hebergement_create_reservation', {
              p_merchant_id: ctx.commerceId,
              p_property_id: propertyId,
              p_unit_id: unitId,
              p_customer_id: linkCustomerId,
              p_guest_name: guestName,
              p_guest_email: guestEmail || null,
              p_guest_phone: guestPhone || null,
              p_check_in: checkIn,
              p_check_out: checkOut,
              p_guests: Number.isFinite(guests) && guests > 0 ? guests : 1,
              p_price_per_night: pricePerNight,
              p_discount: params.discount != null ? Number(params.discount) : 0,
              p_fees: params.fees != null ? Number(params.fees) : 0,
              p_currency: currency,
              p_source: 'ai_assistant',
              p_notes: notes || null,
            });
            if (error) {
              if (/indisponible/i.test(error.message)) throw new Error('Hébergement déjà réservé sur ces dates.');
              if (/dates_invalides/i.test(error.message)) throw new Error('Dates invalides.');
              throw error;
            }
            const resaId = data as number;

            // Relation Réservation → Client (spec 75), best-effort.
            if (linkCustomerId != null && resaId != null) {
              try {
                await supabase.from('core_entity_relations').insert({
                  tenant_id: ctx.tenantId,
                  organization_id: ctx.organizationId,
                  source_type: 'reservation',
                  source_id: String(resaId),
                  target_type: 'customer',
                  target_id: String(linkCustomerId),
                  relation_type: 'belongs_to',
                  label: 'Client de la réservation',
                });
              } catch {
                /* la relation est optionnelle */
              }
            }

            // Événement du socle (émis uniquement à la vraie création —
            // un rejeu idempotent n'exécute pas cette fonction).
            if (resaId != null) {
              try {
                await emitCoreActivity({
                  commerceId: ctx.commerceId,
                  eventKey: 'booking.created',
                  entityType: 'reservation',
                  entityId: resaId,
                  title: `Réservation #${resaId} · ${guestName}`,
                  description: `${checkIn} → ${checkOut}`,
                  amount: pricePerNight,
                  currency,
                  actorUserId: ctx.userId ?? null,
                  actorName: 'Zifek AI',
                  source: 'ai_tool',
                  payload: { property_id: propertyId, unit_id: unitId, source: 'ai_assistant' },
                });
              } catch {
                /* le câblage d'événements ne bloque jamais l'écriture */
              }
            }

            return {
              entityType: 'booking',
              entityId: resaId,
              result: { reservation_id: resaId, property_id: propertyId, unit_id: unitId, price_per_night: pricePerNight },
            };
          },
        });

        if (!action.ok) {
          const denied = action.status === 'denied';
          await log({ authorized: !denied, status: denied ? 'forbidden' : 'error', count: 0, error: action.error });
          return { ...base, authorized: !denied, error: action.error || 'Action refusée par l’Action Layer.' };
        }

        rows = [{
          action_id: action.actionId,
          action_status: action.status,
          entity_id: action.entityId,
          idempotency_key: idempotencyKey,
          reservation: action.result,
        }];
        break;
      }
      // ── Outil d'ÉCRITURE : create_invoice (finance) ──
      // Écriture IA enveloppée par l'Action Layer, comme les rendez-vous.
      case 'create_invoice': {
        const customerName = cleanStr(params.customer_name || params.customer || params.name);
        const customerEmail = cleanStr(params.email);
        const customerPhone = cleanStr(params.phone);
        const amount = params.amount != null ? Number(params.amount) : (params.total != null ? Number(params.total) : null);
        const notes = cleanStr(params.notes);
        const idempotencyKey = cleanStr(params.idempotency_key) || generateIdempotencyKey('inv');

        const missing: string[] = [];
        if (!customerName) missing.push('customer_name');
        if (amount == null || !Number.isFinite(amount)) missing.push('amount');
        if (missing.length > 0) {
          await log({ authorized: true, status: 'validation_error', count: 0, error: `Champs requis : ${missing.join(', ')}` });
          return { ...base, authorized: true, error: `Validation : ${missing.join(', ')} sont requis.` };
        }

        const today = new Date().toISOString().slice(0, 10);
        const invoiceNumber = `FAC-${today.replace(/-/g, '')}-${Math.floor(1000 + Math.random() * 9000)}`;
        const invoiceCurrency = cleanStr(params.currency) || 'MAD';

        const action = await runCoreAction({
          actionKey: 'create_invoice',
          tenantId: ctx.tenantId,
          organizationId: ctx.organizationId,
          actorUserId: ctx.userId ?? null,
          actorName: 'Zifek AI',
          resourceType: 'invoice',
          permission: 'finance.write',
          parameters: params,
          canAccess: ctx.canAccess,
          idempotencyKey,
          idempotencyScope: 'invoices',
          source: 'ai_tool',
          execute: async () => {
            const { data, error } = await supabase
              .from('finance_invoices')
              .insert({
                merchant_id: ctx.commerceId,
                number: invoiceNumber,
                customer_name: customerName,
                customer_email: customerEmail || null,
                customer_phone: customerPhone || null,
                status: 'draft',
                issue_date: today,
                due_date: cleanStr(params.due_date) || null,
                currency: invoiceCurrency,
                subtotal: amount,
                total: amount,
                notes: notes || null,
                created_by: ctx.userId ?? null,
              })
              .select('id, number, status, total, currency')
              .maybeSingle();
            if (error) throw error;
            const row = data as Record<string, unknown> | null;
            const invoiceId = row?.id != null ? String(row.id) : null;

            if (invoiceId) {
              try {
                await emitCoreActivity({
                  commerceId: ctx.commerceId,
                  eventKey: 'invoice.created',
                  entityType: 'invoice',
                  entityId: invoiceId,
                  title: `Facture ${invoiceNumber} · ${customerName}`,
                  amount: amount ?? undefined,
                  currency: invoiceCurrency,
                  actorUserId: ctx.userId ?? null,
                  actorName: 'Zifek AI',
                  source: 'ai_tool',
                });
              } catch {
                /* best-effort */
              }
            }

            return { entityType: 'invoice', entityId: invoiceId, result: { invoice: row } };
          },
        });

        if (!action.ok) {
          const denied = action.status === 'denied';
          await log({ authorized: !denied, status: denied ? 'forbidden' : 'error', count: 0, error: action.error });
          return { ...base, authorized: !denied, error: action.error || 'Action refusée par l’Action Layer.' };
        }

        rows = [{
          action_id: action.actionId,
          action_status: action.status,
          entity_id: action.entityId,
          idempotency_key: idempotencyKey,
          invoice: action.result,
        }];
        break;
      }
      default: {
        await log({ authorized: false, status: 'unknown_tool', count: 0, error: 'Outil inconnu' });
        return { ...base, error: 'Outil inconnu.' };
      }
    }

    await log({ authorized: true, status: 'ok', count: rows.length });
    return { ok: true, authorized: true, tool: toolKey, data: rows, count: rows.length };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Erreur d’exécution.';
    await log({ authorized: true, status: 'error', count: 0, error: message });
    return { ...base, authorized: true, error: message };
  }
}

// ── 45 — Function-calling MULTI-ÉTAPES (le Zifek AI enchaîne plusieurs outils) ──
//
//   Step 1 : search_customers({ q: "Ahmed" })      → alias « customers »
//   Step 2 : get_customer_orders({ customer_id: "{{customers.0.id}}" })
//   Step 3 : get_customer_bookings({ customer_id: "{{customers.0.id}}" })
//
// Les étapes s'exécutent dans l'ordre ; une étape peut référencer le résultat
// d'une étape précédente via {{alias.chemin}} ou {{step_N.chemin}} ou $prev.

export type AiPlanCondition = 'always' | 'has_results' | 'no_results';

export interface AiPlanStep {
  /** Clé d'outil (ex : search_customers). */
  tool: string;
  /** Paramètres, valeurs scalaires possibly référençant une étape précédente. */
  params?: Record<string, unknown>;
  /** Nom court réutilisable dans les références {{alias}}. */
  alias?: string;
  /** Exécution conditionnée au résultat de l'étape précédente. */
  ifPrevious?: AiPlanCondition;
}

export interface AiPlanStepResult {
  index: number;
  tool: string;
  alias: string;
  ok: boolean;
  authorized: boolean;
  count: number;
  error?: string;
  data: unknown[];
}

export interface AiPlanResult {
  ok: boolean;
  steps: AiPlanStepResult[];
  stoppedAt?: number;
  error?: string;
}

/** Résout « alias.chemin » (ou step_N, $prev, $last) dans le magasin d'étapes. */
function resolveReference(store: Record<string, unknown>, expr: string): unknown {
  const [alias, ...rest] = expr.split('.').map((s) => s.trim());
  let cur: unknown = store[alias];
  for (const seg of rest) {
    if (cur == null) return undefined;
    if (Array.isArray(cur)) {
      if (/^\d+$/.test(seg)) cur = cur[Number(seg)];
      else cur = (cur[0] as Record<string, unknown> | undefined)?.[seg];
    } else if (typeof cur === 'object') {
      cur = (cur as Record<string, unknown>)[seg];
    } else {
      return undefined;
    }
  }
  return cur;
}

/** Remplace toutes les références {{...}} d'une valeur (scalaire ou imbriquée). */
function resolveParams(value: unknown, store: Record<string, unknown>): unknown {
  if (typeof value === 'string') {
    const full = value.match(/^\{\{\s*([^}]+?)\s*\}\}$/);
    if (full) {
      const resolved = resolveReference(store, full[1]);
      return resolved === undefined ? null : resolved;
    }
    return value.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_m, expr: string) => {
      const resolved = resolveReference(store, expr);
      return resolved === undefined || resolved === null ? '' : String(resolved);
    });
  }
  if (Array.isArray(value)) return value.map((v) => resolveParams(v, store));
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
      out[k] = resolveParams(v, store);
    });
    return out;
  }
  return value;
}

/**
 * Exécute un plan multi-étapes : chaque étape passe par l'autorisation, la
 * validation et la journalisation d'`invokeAiTool`. Les résultats alimentent
 * un contexte réutilisable par les étapes suivantes.
 */
export async function invokeAiToolChain(
  steps: AiPlanStep[],
  ctx: AiToolContext,
  opts?: { maxSteps?: number },
): Promise<AiPlanResult> {
  const max = Math.max(1, Math.min(opts?.maxSteps ?? 10, 25));
  const limited = steps.slice(0, max);
  const store: Record<string, unknown> = {};
  const results: AiPlanStepResult[] = [];
  let prevCount = 0;

  for (let i = 0; i < limited.length; i += 1) {
    const step = limited[i];
    const alias = (step.alias || `step_${i}`).trim() || `step_${i}`;
    const tool = String(step.tool || '').trim();

    // Condition d'exécution basée sur le résultat précédent.
    const cond = step.ifPrevious || 'always';
    if (i > 0 && ((cond === 'has_results' && prevCount === 0) || (cond === 'no_results' && prevCount > 0))) {
      results.push({ index: i, tool, alias, ok: false, authorized: false, count: 0, error: 'Étape ignorée (condition non remplie).', data: [] });
      continue;
    }

    const resolvedParams = resolveParams(step.params || {}, store) as Record<string, unknown>;
    const res = await invokeAiTool(tool, resolvedParams, ctx);

    store[alias] = res.data;
    store[`step_${i}`] = res.data;
    store.$prev = res.data;
    store.$last = res.data;
    prevCount = res.count;

    results.push({
      index: i,
      tool,
      alias,
      ok: res.ok,
      authorized: res.authorized,
      count: res.count,
      error: res.error,
      data: res.ok ? res.data : [],
    });

    // Stop si une étape échoue de manière bloquante (autorisation/outil inconnu).
    if (!res.ok && !res.authorized) {
      return { ok: false, steps: results, stoppedAt: i, error: res.error };
    }
  }

  return { ok: results.every((r) => r.ok || r.error === 'Étape ignorée (condition non remplie).'), steps: results };
}

// ── Journal des appels ──

export interface AiToolCall {
  id: number;
  tool_key: string;
  input: Record<string, unknown> | null;
  result_count: number | null;
  status: string | null;
  authorized: boolean | null;
  actor_user_id: number | null;
  source: string | null;
  error: string | null;
  duration_ms: number | null;
  created_at: string | null;
}

export async function listAiToolCalls(organizationId: number, limit = 60): Promise<AiToolCall[]> {
  const { data, error } = await supabase
    .from('core_ai_tool_calls')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []) as AiToolCall[];
}

export interface AiToolStats {
  total: number;
  authorized: number;
  forbidden: number;
  byTool: Record<string, number>;
}

export async function fetchAiToolStats(organizationId: number): Promise<AiToolStats> {
  const { data } = await supabase
    .from('core_ai_tool_calls')
    .select('tool_key, authorized, status')
    .eq('organization_id', organizationId);
  const byTool: Record<string, number> = {};
  let authorized = 0;
  let forbidden = 0;
  (data || []).forEach((r) => {
    const row = r as { tool_key: string; authorized: boolean | null; status: string | null };
    byTool[row.tool_key] = (byTool[row.tool_key] || 0) + 1;
    if (row.authorized) authorized += 1;
    else forbidden += 1;
  });
  return { total: (data || []).length, authorized, forbidden, byTool };
}

export function toolCallStatusMeta(status: string | null | undefined): { label: string; className: string } {
  switch (status) {
    case 'ok': return { label: 'OK', className: 'bg-primary-100 text-primary-900' };
    case 'forbidden': return { label: 'Refusé', className: 'bg-red-100 text-red-800' };
    case 'disabled': return { label: 'Désactivé', className: 'bg-background-100 text-foreground-500' };
    case 'validation_error': return { label: 'Validation', className: 'bg-accent-100 text-accent-900' };
    case 'unknown_tool': return { label: 'Inconnu', className: 'bg-background-100 text-foreground-500' };
    case 'error': return { label: 'Erreur', className: 'bg-red-100 text-red-800' };
    default: return { label: status || '—', className: 'bg-background-100 text-foreground-500' };
  }
}